import { useState, useMemo } from 'react'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  PieChart,
  AlertCircle,
  CheckCircle,
  FileText,
  CreditCard,
} from 'lucide-react'

// Mock financial data
const mockFinancialData = {
  period: 'This Month',
  revenue: [
    { source: 'Product Sales', amount: 45600, trend: 'up', percentage: 35 },
    { source: 'Services', amount: 32400, trend: 'up', percentage: 25 },
    { source: 'Consulting', amount: 19800, trend: 'down', percentage: 15 },
    { source: 'Subscriptions', amount: 26000, trend: 'up', percentage: 20 },
    { source: 'Training', amount: 7200, trend: 'up', percentage: 5 },
  ],
  expenses: [
    { category: 'Cloud Infrastructure', amount: 8500, budget: 10000, status: 'under' },
    { category: 'Salaries & Payroll', amount: 65000, budget: 65000, status: 'on-track' },
    { category: 'Marketing & Ads', amount: 12000, budget: 15000, status: 'under' },
    { category: 'Software & Tools', amount: 4200, budget: 5000, status: 'under' },
    { category: 'Utilities & Hosting', amount: 3500, budget: 4000, status: 'under' },
    { category: 'Operations', amount: 6800, budget: 7500, status: 'under' },
  ],
  invoices: [
    {
      id: 'INV-2024-001',
      client: 'TechCorp Solutions',
      amount: 15000,
      status: 'paid',
      dueDate: '2024-04-15',
      issuedDate: '2024-03-15',
    },
    {
      id: 'INV-2024-002',
      client: 'Digital Agency Inc',
      amount: 8500,
      status: 'overdue',
      dueDate: '2024-03-20',
      issuedDate: '2024-02-20',
    },
    {
      id: 'INV-2024-003',
      client: 'Enterprise Systems',
      amount: 22000,
      status: 'pending',
      dueDate: '2024-05-15',
      issuedDate: '2024-04-15',
    },
    {
      id: 'INV-2024-004',
      client: 'StartUp Partners',
      amount: 5000,
      status: 'paid',
      dueDate: '2024-04-10',
      issuedDate: '2024-03-10',
    },
  ],
  accounts: [
    { name: 'Primary Business', balance: 125400, type: 'checking', institution: 'Chase' },
    { name: 'Operating Reserve', balance: 45600, type: 'savings', institution: 'Bank of America' },
    { name: 'Tax Reserve', balance: 28500, type: 'savings', institution: 'Wells Fargo' },
  ],
}

export default function FinancialMonitorPage() {
  const [timeRange, setTimeRange] = useState('month')
  const [expandedSection, setExpandedSection] = useState('overview')

  // Calculate totals
  const totalRevenue = useMemo(() => {
    return mockFinancialData.revenue.reduce((sum, item) => sum + item.amount, 0)
  }, [])

  const totalExpenses = useMemo(() => {
    return mockFinancialData.expenses.reduce((sum, item) => sum + item.amount, 0)
  }, [])

  const netProfit = totalRevenue - totalExpenses
  const profitMargin = ((netProfit / totalRevenue) * 100).toFixed(1)

  const totalBudget = useMemo(() => {
    return mockFinancialData.expenses.reduce((sum, item) => sum + item.budget, 0)
  }, [])

  const budgetUtilization = ((totalExpenses / totalBudget) * 100).toFixed(1)

  const overdueBills = mockFinancialData.invoices.filter(i => i.status === 'overdue').length
  const totalBankBalance = mockFinancialData.accounts.reduce((sum, acc) => sum + acc.balance, 0)

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Financial Monitor</h1>
          <p className="text-blue-200 mt-1">Complete financial overview and AI-powered insights</p>
        </div>
      </div>

      {/* Alert Banners */}
      {overdueBills > 0 && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-red-300">Overdue Invoices</h3>
            <p className="text-sm text-red-200 mt-1">
              {overdueBills} invoice{overdueBills !== 1 ? 's' : ''} overdue. Total: $
              {mockFinancialData.invoices
                .filter(i => i.status === 'overdue')
                .reduce((sum, i) => sum + i.amount, 0)
                .toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* Time Range Selector */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
        <label className="block text-sm font-medium text-blue-200 mb-3">Reporting Period</label>
        <div className="flex gap-2">
          {['week', 'month', 'quarter', 'year'].map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                timeRange === range
                  ? 'bg-green-600 text-white'
                  : 'bg-slate-700/50 text-blue-200 hover:bg-slate-700/70 border border-blue-500/30'
              }`}
            >
              {range === 'week'
                ? 'This Week'
                : range === 'month'
                  ? 'This Month'
                  : range === 'quarter'
                    ? 'This Quarter'
                    : 'This Year'}
            </button>
          ))}
        </div>
      </div>

      {/* Key Financial Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Total Revenue</p>
            <TrendingUp className="text-green-400" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">${totalRevenue.toLocaleString()}</p>
          <p className="text-xs text-green-400 mt-2">↑ 12% vs last month</p>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Total Expenses</p>
            <TrendingDown className="text-orange-400" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">${totalExpenses.toLocaleString()}</p>
          <p className="text-xs text-orange-400 mt-2">↓ 5% vs last month</p>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Net Profit</p>
            <DollarSign className="text-blue-400" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">${netProfit.toLocaleString()}</p>
          <p className="text-xs text-blue-400 mt-2">Margin: {profitMargin}%</p>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Bank Balance</p>
            <CreditCard className="text-purple-400" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">${totalBankBalance.toLocaleString()}</p>
          <p className="text-xs text-purple-400 mt-2">Across {mockFinancialData.accounts.length} accounts</p>
        </div>
      </div>

      {/* Revenue & Expenses Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Breakdown */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <TrendingUp className="text-green-400" size={24} />
            <h2 className="text-xl font-bold text-white">Revenue Sources</h2>
          </div>
          <div className="space-y-3">
            {mockFinancialData.revenue.map((source, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{source.source}</p>
                  <div className="w-full bg-slate-700/50 rounded-full h-2 mt-1 border border-blue-500/20">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${source.percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <p className="text-sm font-bold text-white">${source.amount.toLocaleString()}</p>
                  <p className="text-xs text-blue-300/70">{source.percentage}%</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-blue-500/30">
            <p className="text-sm text-blue-200">
              Total Revenue: <span className="font-bold text-green-400">${totalRevenue.toLocaleString()}</span>
            </p>
          </div>
        </div>

        {/* Budget vs Actual */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <PieChart className="text-blue-400" size={24} />
            <h2 className="text-xl font-bold text-white">Budget vs Actual</h2>
          </div>
          <div className="space-y-3">
            {mockFinancialData.expenses.map((expense, idx) => {
              const utilization = ((expense.amount / expense.budget) * 100).toFixed(0)
              const isOver = expense.amount > expense.budget
              return (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-white">{expense.category}</p>
                    <span
                      className={`text-xs font-bold px-2 py-1 rounded ${
                        isOver
                          ? 'bg-red-500/30 text-red-200'
                          : expense.status === 'under'
                            ? 'bg-green-500/30 text-green-200'
                            : 'bg-blue-500/30 text-blue-200'
                      }`}
                    >
                      {utilization}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-700/50 rounded-full h-2 border border-blue-500/20">
                    <div
                      className={isOver ? 'bg-red-500' : 'bg-blue-500'}
                      style={{ width: `${Math.min(utilization, 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-blue-300/70 mt-1">
                    <span>${expense.amount.toLocaleString()}</span>
                    <span>Budget: ${expense.budget.toLocaleString()}</span>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-blue-500/30">
            <p className="text-sm text-blue-200">
              Total Expenses: <span className="font-bold text-white">${totalExpenses.toLocaleString()}</span>
              <br />
              Budget Utilization: <span className="font-bold text-blue-400">{budgetUtilization}%</span>
            </p>
          </div>
        </div>
      </div>

      {/* Invoices & Payments */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <FileText className="text-orange-400" size={24} />
          <h2 className="text-xl font-bold text-white">Invoices & Payments</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-blue-500/30">
                <th className="text-left py-3 px-3 font-bold text-white">Invoice</th>
                <th className="text-left py-3 px-3 font-bold text-white">Client</th>
                <th className="text-right py-3 px-3 font-bold text-white">Amount</th>
                <th className="text-left py-3 px-3 font-bold text-white">Due Date</th>
                <th className="text-left py-3 px-3 font-bold text-white">Status</th>
              </tr>
            </thead>
            <tbody>
              {mockFinancialData.invoices.map(invoice => (
                <tr key={invoice.id} className="border-b border-blue-500/20 hover:bg-slate-700/50">
                  <td className="py-3 px-3 font-mono text-white">{invoice.id}</td>
                  <td className="py-3 px-3 text-blue-200">{invoice.client}</td>
                  <td className="py-3 px-3 text-right font-bold text-white">
                    ${invoice.amount.toLocaleString()}
                  </td>
                  <td className="py-3 px-3 text-blue-200">{new Date(invoice.dueDate).toLocaleDateString()}</td>
                  <td className="py-3 px-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold ${
                        invoice.status === 'paid'
                          ? 'bg-green-500/30 text-green-200'
                          : invoice.status === 'pending'
                            ? 'bg-blue-500/30 text-blue-200'
                            : 'bg-red-500/30 text-red-200'
                      }`}
                    >
                      {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bank Accounts */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <CreditCard className="text-purple-400" size={24} />
          <h2 className="text-xl font-bold text-white">Bank Accounts</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {mockFinancialData.accounts.map((account, idx) => (
            <div key={idx} className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-lg p-4 border border-purple-500/30">
              <p className="text-sm text-blue-200 mb-1">{account.institution}</p>
              <h3 className="font-bold text-white mb-2">{account.name}</h3>
              <p className="text-2xl font-bold text-white mb-1">${account.balance.toLocaleString()}</p>
              <p className="text-xs text-blue-300/70 capitalize">{account.type} Account</p>
            </div>
          ))}
        </div>
      </div>

      {/* AI Financial Insights */}
      <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">📈 AI Financial Insights (Groq)</h2>
        <div className="space-y-3">
          <div className="bg-slate-800/50 rounded p-3 border-l-4 border-green-500">
            <p className="text-sm text-white">
              <strong>✓ Excellent Profit Margin:</strong> Your profit margin is {profitMargin}%, which is above industry
              average. Continue focusing on high-margin services.
            </p>
          </div>
          <div className="bg-slate-800/50 rounded p-3 border-l-4 border-blue-500">
            <p className="text-sm text-white">
              <strong>💡 Revenue Opportunity:</strong> Product Sales are your highest revenue source (35%). Consider
              expanding this line of business.
            </p>
          </div>
          <div className="bg-slate-800/50 rounded p-3 border-l-4 border-orange-500">
            <p className="text-sm text-white">
              <strong>⚠️ Budget Alert:</strong> {overdueBills} invoices overdue. Recommended: Send payment reminders and
              offer payment plans.
            </p>
          </div>
          <div className="bg-slate-800/50 rounded p-3 border-l-4 border-purple-500">
            <p className="text-sm text-white">
              <strong>🎯 Cost Optimization:</strong> You're utilizing {budgetUtilization}% of your budget. Potential to
              allocate additional {((totalBudget - totalExpenses) / totalBudget * 100).toFixed(0)}% to growth initiatives.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
